import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import { useCheckout } from "./ui";

export function StickyCta() {
  const [show, setShow] = useState(false);
  const openCheckout = useCheckout();

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90 }}
          animate={{ y: 0 }}
          exit={{ y: 90 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] md:hidden"
        >
          <div className="flex items-center justify-between gap-4 border-t border-black/10 bg-white/95 px-5 py-3.5 backdrop-blur-2xl">
            <div>
              <p className="font-serif text-xl font-semibold leading-none text-hc-ink">R$ 97</p>
              <p className="mt-0.5 font-dmsans text-[10px] font-semibold uppercase tracking-[0.14em] text-hc-mutedInk">
                12 meses · 1 pagamento
              </p>
            </div>
            <button
              data-testid="sticky-cta"
              onClick={openCheckout}
              className="rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim px-6 py-3 font-dmsans text-[11px] font-semibold uppercase tracking-[0.12em] text-white transition-[filter] duration-300 hover:brightness-110"
            >
              Quero o MOM
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CheckoutModal({ open, onClose }) {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", whatsapp: "" });

  useEffect(() => {
    if (!open) setDone(false);
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    setDone(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <motion.div
            data-testid="checkout-modal"
            initial={{ y: 70, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 70, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-8 text-hc-ink sm:max-w-md sm:rounded-3xl sm:p-10"
          >
            <button
              data-testid="checkout-close"
              onClick={onClose}
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-hc-off text-hc-mutedInk transition-colors hover:text-hc-ink"
            >
              <X className="h-4 w-4" />
            </button>

            {done ? (
              <div data-testid="checkout-success" className="py-6 text-center">
                <CheckCircle2 className="mx-auto h-14 w-14 text-mom-vinho" />
                <h3 className="mt-6 font-serif text-2xl font-semibold text-hc-ink">
                  Pedido simulado!
                </h3>
                <p className="mt-4 font-dmsans text-sm leading-relaxed text-hc-mutedInk">
                  Esta é uma demonstração. Em produção, você seguiria daqui para o
                  pagamento de <span className="font-semibold text-hc-ink">R$ 97</span> e
                  receberia o acesso por e-mail.
                </p>
                <button
                  data-testid="checkout-back"
                  onClick={onClose}
                  className="mt-8 rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim px-7 py-3.5 font-dmsans text-xs font-semibold uppercase tracking-[0.14em] text-white transition-[filter] hover:brightness-110"
                >
                  Voltar para a página
                </button>
              </div>
            ) : (
              <>
                <p className="font-dmsans text-[10px] font-semibold uppercase tracking-[0.24em] text-mom-vinho">
                  Checkout · demonstração
                </p>
                <h3 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-hc-ink">
                  Seu salão com o MOM
                </h3>
                <div className="mt-5 flex items-baseline justify-between rounded-2xl bg-hc-off px-5 py-4">
                  <span className="font-dmsans text-sm text-hc-mutedInk">12 meses de acesso</span>
                  <span className="font-serif text-xl font-semibold text-hc-ink">
                    <span className="mr-2 font-dmsans text-sm font-normal text-hc-mutedInk line-through">R$ 197</span>
                    R$ 97
                  </span>
                </div>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="ck-nome" className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.14em] text-hc-mutedInk">Nome</label>
                    <input
                      id="ck-nome"
                      data-testid="checkout-nome"
                      required
                      value={form.nome}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })}
                      placeholder="Como te chamam no salão"
                      className="mt-1.5 w-full rounded-xl border border-black/10 bg-hc-off px-4 py-3 font-dmsans text-sm text-hc-ink outline-none transition-colors placeholder:text-hc-mutedInk/50 focus:border-mom-carmim"
                    />
                  </div>
                  <div>
                    <label htmlFor="ck-email" className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.14em] text-hc-mutedInk">E-mail</label>
                    <input
                      id="ck-email"
                      data-testid="checkout-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Para receber o acesso"
                      className="mt-1.5 w-full rounded-xl border border-black/10 bg-hc-off px-4 py-3 font-dmsans text-sm text-hc-ink outline-none transition-colors placeholder:text-hc-mutedInk/50 focus:border-mom-carmim"
                    />
                  </div>
                  <div>
                    <label htmlFor="ck-zap" className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.14em] text-hc-mutedInk">WhatsApp</label>
                    <input
                      id="ck-zap"
                      data-testid="checkout-whatsapp"
                      required
                      value={form.whatsapp}
                      onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                      placeholder="(00) 90000-0000"
                      className="mt-1.5 w-full rounded-xl border border-black/10 bg-hc-off px-4 py-3 font-dmsans text-sm text-hc-ink outline-none transition-colors placeholder:text-hc-mutedInk/50 focus:border-mom-carmim"
                    />
                  </div>
                  <button
                    data-testid="checkout-submit-button"
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim py-4 font-dmsans text-xs font-semibold uppercase tracking-[0.16em] text-white transition-[filter] duration-300 hover:brightness-110"
                  >
                    Simular compra — R$ 97
                  </button>
                  <p className="text-center font-dmsans text-[11px] text-hc-mutedInk">
                    Garantia incondicional de 7 dias · Nenhum pagamento real nesta demo
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
